/**
 * Authentication Service
 * Handles Supabase Auth operations: login, logout, signup, invite, password reset, MFA
 */

import { supabase } from '@shared/supabaseClient';
import { debug } from '@shared/utils/debug';

const log = debug.withPrefix('AuthService');

/**
 * Record a login event in login_history table
 * @param {object} options - Login event details
 */
const recordLoginHistory = async ({ userId, authId, loginMethod = 'password', success = true, failureReason = null }) => {
  try {
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
    
    // Insert new login record
    await supabase.from('login_history').insert({
      user_id: userId,
      auth_id: authId,
      login_time: new Date().toISOString(),
      user_agent: userAgent,
      login_method: loginMethod,
      success,
      failure_reason: failureReason,
    });
    
    // Keep only last 5 login records per user to prevent table bloat
    const { data: allRecords } = await supabase
      .from('login_history')
      .select('id')
      .eq('user_id', userId)
      .order('login_time', { ascending: false });
    
    if (allRecords && allRecords.length > 5) {
      // Delete all but the 5 most recent
      const idsToDelete = allRecords.slice(5).map(r => r.id);
      await supabase
        .from('login_history')
        .delete()
        .in('id', idsToDelete);
      log.info('Cleaned up old login history, kept last 5 entries');
    }
    
    log.info('Login history recorded');
  } catch (err) {
    // Don't fail login if history recording fails
    log.warn('Failed to record login history:', err);
  }
};

/**
 * Sign in with email and password
 * Returns MFA challenge if MFA is enabled for the user
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise<{user: object, profile: object, mfaRequired: boolean, factorId: string?}>}
 */
export const signIn = async (email, password) => {
  log.info('Attempting sign in for:', email);
  
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  
  if (error) {
    log.error('Sign in failed:', error);
    throw error;
  }
  
  // Check if MFA is required
  const { data: factorsData } = await supabase.auth.mfa.listFactors();
  const totpFactor = factorsData?.totp?.find(f => f.status === 'verified');
  const webauthnFactor = factorsData?.all?.find(f => f.factor_type === 'webauthn' && f.status === 'verified');
  
  // Determine which MFA methods are available
  const availableMethods = [];
  if (totpFactor) availableMethods.push({ type: 'totp', factorId: totpFactor.id });
  if (webauthnFactor) availableMethods.push({ type: 'webauthn', factorId: webauthnFactor.id });
  
  if (availableMethods.length > 0) {
    log.info('MFA required for user, methods:', availableMethods.map(m => m.type));
    
    // Create MFA challenge for the first available method (prefer WebAuthn for convenience)
    const preferredMethod = webauthnFactor ? { type: 'webauthn', factorId: webauthnFactor.id } : availableMethods[0];
    
    const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
      factorId: preferredMethod.factorId,
    });
    
    if (challengeError) {
      log.error('MFA challenge failed:', challengeError);
      throw challengeError;
    }
    
    return {
      user: data.user,
      profile: null,
      mfaRequired: true,
      factorId: preferredMethod.factorId,
      factorType: preferredMethod.type,
      challengeId: challengeData.id,
      availableMethods,
    };
  }
  
  // No MFA - proceed with profile fetch
  const profile = await getUserProfile(data.user.id, data.user.email);
  
  if (!profile) {
    log.error('No profile found for authenticated user');
    await supabase.auth.signOut();
    throw new Error('User profile not found. Please contact your administrator.');
  }
  
  if (!profile.isActive) {
    log.error('User account is deactivated');
    await supabase.auth.signOut();
    throw new Error('Your account has been deactivated. Please contact your administrator.');
  }
  
  // Update last login
  await supabase
    .from('users')
    .update({ last_login: new Date().toISOString() })
    .eq('auth_id', data.user.id);
  
  // Record login history
  await recordLoginHistory({
    userId: profile.dbId || profile.id,
    authId: data.user.id,
    loginMethod: 'password',
    success: true,
  });
  
  log.info('Sign in successful:', profile.username);
  
  return {
    user: data.user,
    profile,
    mfaRequired: false,
    requiresPasswordChange: profile.requiresPasswordChange || false,
  };
};

/**
 * Verify MFA code and complete sign in
 * @param {string} factorId - The MFA factor ID
 * @param {string} challengeId - The challenge ID from signIn
 * @param {string} code - The 6-digit TOTP code
 * @returns {Promise<{user: object, profile: object}>}
 */
export const verifyMfa = async (factorId, challengeId, code) => {
  log.info('Verifying MFA code');
  
  const { data: _data, error } = await supabase.auth.mfa.verify({
    factorId,
    challengeId,
    code,
  });
  
  if (error) {
    log.error('MFA verification failed:', error);
    throw new Error('Invalid verification code. Please try again.');
  }
  
  // Get user and profile
  const { data: { user } } = await supabase.auth.getUser();
  const profile = await getUserProfile(user.id, user.email);
  
  if (!profile) {
    await supabase.auth.signOut();
    throw new Error('User profile not found. Please contact your administrator.');
  }
  
  if (!profile.isActive) {
    await supabase.auth.signOut();
    throw new Error('Your account has been deactivated. Please contact your administrator.');
  }
  
  // Update last login
  await supabase
    .from('users')
    .update({ last_login: new Date().toISOString() })
    .eq('auth_id', user.id);
  
  // Record login history with MFA method
  await recordLoginHistory({
    userId: profile.dbId || profile.id,
    authId: user.id,
    loginMethod: 'mfa_totp',
    success: true,
  });
  
  log.info('MFA verification successful');
  
  return { user, profile };
};

/**
 * Enroll in MFA (TOTP)
 * Returns QR code and secret for authenticator app
 * @returns {Promise<{factorId: string, qrCode: string, secret: string}>}
 */
export const enrollMfa = async () => {
  log.info('Enrolling in MFA');
  
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: 'Authenticator App',
  });
  
  if (error) {
    log.error('MFA enrollment failed:', error);
    throw error;
  }
  
  return {
    factorId: data.id,
    qrCode: data.totp.qr_code,
    secret: data.totp.secret,
  };
};

/**
 * Verify and activate MFA enrollment
 * @param {string} factorId - The factor ID from enrollment
 * @param {string} code - The 6-digit verification code
 */
export const verifyMfaEnrollment = async (factorId, code) => {
  log.info('Verifying MFA enrollment');
  
  // Create a challenge first
  const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
    factorId,
  });
  
  if (challengeError) {
    log.error('MFA challenge failed:', challengeError);
    throw challengeError;
  }
  
  // Verify the code
  const { error } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challengeData.id,
    code,
  });
  
  if (error) {
    log.error('MFA verification failed:', error);
    throw new Error('Invalid verification code. Please try again.');
  }
  
  log.info('MFA enrollment verified and activated');
};

/**
 * Unenroll from MFA
 * @param {string} factorId - The factor ID to remove
 */
export const unenrollMfa = async (factorId) => {
  log.info('Unenrolling from MFA');
  
  const { error } = await supabase.auth.mfa.unenroll({
    factorId,
  });
  
  if (error) {
    log.error('MFA unenrollment failed:', error);
    throw error;
  }
  
  log.info('MFA unenrolled successfully');
};

/**
 * Get current MFA status for the logged-in user
 * @returns {Promise<{enabled: boolean, totpFactorId: string?, webauthnFactorId: string?, methods: string[]}>}
 */
export const getMfaStatus = async () => {
  const { data, error } = await supabase.auth.mfa.listFactors();
  
  if (error) {
    log.error('Failed to get MFA status:', error);
    return { enabled: false, totpFactorId: null, webauthnFactorId: null, methods: [] };
  }
  
  const verifiedTotp = data?.totp?.find(f => f.status === 'verified');
  const verifiedWebAuthn = data?.phone?.find(f => f.factor_type === 'webauthn' && f.status === 'verified');
  
  const methods = [];
  if (verifiedTotp) methods.push('totp');
  if (verifiedWebAuthn) methods.push('webauthn');
  
  return {
    enabled: methods.length > 0,
    totpFactorId: verifiedTotp?.id || null,
    webauthnFactorId: verifiedWebAuthn?.id || null,
    methods,
    // Legacy compatibility
    factorId: verifiedTotp?.id || verifiedWebAuthn?.id || null,
  };
};

/**
 * Enroll in WebAuthn (passkey/fingerprint)
 * @returns {Promise<{factorId: string, options: object}>}
 */
export const enrollWebAuthn = async () => {
  log.info('Enrolling in WebAuthn');
  
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'webauthn',
    friendlyName: 'Passkey / Fingerprint',
  });
  
  if (error) {
    log.error('WebAuthn enrollment failed:', error);
    throw error;
  }
  
  return {
    factorId: data.id,
    options: data.webauthn,
  };
};

/**
 * Verify WebAuthn enrollment with browser credential
 * @param {string} factorId - The factor ID from enrollment
 * @returns {Promise<void>}
 */
export const verifyWebAuthnEnrollment = async (factorId) => {
  log.info('Verifying WebAuthn enrollment');
  
  // Create a challenge
  const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
    factorId,
  });
  
  if (challengeError) {
    log.error('WebAuthn challenge failed:', challengeError);
    throw challengeError;
  }
  
  // The browser will handle the credential assertion
  const { error } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challengeData.id,
  });
  
  if (error) {
    log.error('WebAuthn verification failed:', error);
    throw new Error('Passkey verification failed. Please try again.');
  }
  
  log.info('WebAuthn enrollment verified and activated');
};

/**
 * Verify MFA using WebAuthn (passkey/fingerprint)
 * @param {string} factorId - The WebAuthn factor ID
 * @param {string} challengeId - The challenge ID from signIn
 * @returns {Promise<{user: object, profile: object}>}
 */
export const verifyWebAuthn = async (factorId, challengeId) => {
  log.info('Verifying WebAuthn');
  
  const { data: _data, error } = await supabase.auth.mfa.verify({
    factorId,
    challengeId,
  });
  
  if (error) {
    log.error('WebAuthn verification failed:', error);
    throw new Error('Passkey verification failed. Please try again.');
  }
  
  // Get user and profile
  const { data: { user } } = await supabase.auth.getUser();
  const profile = await getUserProfile(user.id, user.email);
  
  if (!profile) {
    await supabase.auth.signOut();
    throw new Error('User profile not found. Please contact your administrator.');
  }
  
  if (!profile.isActive) {
    await supabase.auth.signOut();
    throw new Error('Your account has been deactivated. Please contact your administrator.');
  }
  
  // Update last login
  await supabase
    .from('users')
    .update({ last_login: new Date().toISOString() })
    .eq('auth_id', user.id);
  
  // Record login history with WebAuthn method
  await recordLoginHistory({
    userId: profile.dbId || profile.id,
    authId: user.id,
    loginMethod: 'mfa_webauthn',
    success: true,
  });
  
  log.info('WebAuthn verification successful');
  
  return { user, profile };
};

/**
 * Sign out the current user
 */
export const signOut = async () => {
  log.info('Signing out');
  const { error } = await supabase.auth.signOut();
  if (error) {
    log.error('Sign out failed:', error);
    throw error;
  }
  log.info('Sign out successful');
};

/**
 * Get current session
 * @returns {Promise<{session: object, profile: object} | null>}
 */
export const getSession = async () => {
  try {
    // Add timeout to prevent hanging on slow/unstable networks
    const timeoutMs = 8000; // 8 second timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    const { data: { session }, error } = await supabase.auth.getSession();
    clearTimeout(timeoutId);
    
    if (error) {
      log.error('Failed to get session:', error);
      return null;
    }
    
    if (!session) {
      return null;
    }
    
    // Fetch user profile (try auth_id first, then email fallback)
    const profile = await getUserProfile(session.user.id, session.user.email);
    
    if (!profile || !profile.isActive) {
      // Profile doesn't exist or is deactivated
      await supabase.auth.signOut();
      return null;
    }
    
    return { session, profile };
  } catch (error) {
    if (error.name === 'AbortError') {
      log.warn('Session check timed out - likely network issue');
    } else {
      log.error('Session check failed:', error);
    }
    return null;
  }
};

/**
 * Get user profile from app_users by auth_id or email (fallback for migration)
 * @param {string} authId - Supabase Auth user ID
 * @param {string} email - User's email (fallback lookup)
 * @returns {Promise<object | null>}
 */
export const getUserProfile = async (authId, email = null) => {
  // Use users table directly for depot-induction database
  return await getUserProfileFromUsers(authId, email);
};

const getUserProfileFromUsers = async (authId, email = null) => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('auth_id', authId)
    .single();

  if (error && error.code === 'PGRST116' && email) {
    const { data: emailData, error: emailError } = await supabase
      .from('users')
      .select('*')
      .eq('email', email)
      .single();

    if (!emailError && emailData) {
      return mapLegacyUserProfile({ ...emailData, auth_id: authId });
    }
  }

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    log.error('Failed to fetch user profile from users table:', error);
    throw error;
  }

  return mapLegacyUserProfile(data);
};

function mapLegacyUserProfile(record) {
  if (!record) return null;

  const firstName = record.first_name || '';
  const surname = record.surname || '';
  const fullName = [firstName, surname].filter(Boolean).join(' ');
  const displayName = fullName || record.email || 'User';

  return {
    id: record.id,
    dbId: record.id,
    authId: record.auth_id,
    username: record.email,
    email: record.email,
    firstName,
    surname,
    name: displayName,
    role: record.role,
    company: record.company || null,
    // Note: Company logo is managed via SystemSettings → localStorage, not per-user DB columns
    isActive: record.is_active !== false,
    isPlatformOwner: record.is_platform_owner === true,
    requiresPasswordChange: record.requires_password_change || false,
    lastLogin: record.last_login ? new Date(record.last_login) : null,
    invitedAt: record.invited_at ? new Date(record.invited_at) : null,
    invitedBy: record.invited_by || null,
    createdAt: record.created_at ? new Date(record.created_at) : null,
    competencyLevel: record.competency_level || 1,
    status: record.is_active === false ? 'inactive' : 'active',
    complianceMode: record.compliance_mode ?? 'flexible',
    customGracePeriods: record.custom_grace_periods ?? null,
    missedTestsVisibility: record.missed_tests_visibility ?? 'full',
    depot_id: record.depot_id || null,
  };
}

/**
 * Invite a new user (Admin/Team Leader only)
 * Creates a profile in users and sends password reset email so user can set their password
 * @param {object} userData - New user data
 * @param {string} invitedByUserId - ID of the user sending the invite
 * @returns {Promise<object>} Created profile
 */
export const inviteUser = async (userData, invitedByUserId) => {
  log.info('Inviting user:', userData.email);

  // Use admin-provided temp password, or generate one as fallback
  const tempPassword = userData.tempPassword?.trim() || generateTempPassword();

  // Create the auth user via the admin-create-user Edge Function.
  // This uses the service-role key server-side so it bypasses Supabase's
  // client-side email rate limits and skips confirmation-email sending entirely.
  const { data: fnData, error: fnError } = await supabase.functions.invoke('admin-create-user', {
    body: {
      email:            userData.email.toLowerCase().trim(),
      password:         tempPassword,
      firstName:        userData.firstName || '',
      surname:          userData.surname || '',
      role:             userData.role || 'employee',
      company:          userData.company || '',
      competencyLevel:  userData.competencyLevel || 1,
      invitedBy:        invitedByUserId || null,
    },
  });

  if (fnError || !fnData?.ok) {
    const msg = fnData?.error || fnError?.message || 'Unknown error';
    log.error('admin-create-user function error:', msg);
    if (msg.includes('already exists')) {
      throw new Error('A user with this email already exists.');
    }
    throw new Error(`Failed to create user: ${msg}`);
  }

  // Wrap in the same shape the rest of the function expects
  const authData = { user: fnData.user };
  
  // Create the user profile via SECURITY DEFINER RPC — bypasses RLS so the
  // admin's session can insert a new row without permission errors.
  // Falls back to direct insert if the function doesn't exist yet (pre-migration).
  let profile;
  let profileError;

  const { data: rpcProfile, error: rpcError } = await supabase.rpc('admin_create_user_profile', {
    p_auth_id:          authData.user?.id,
    p_email:            userData.email.toLowerCase().trim(),
    p_first_name:       userData.firstName || '',
    p_surname:          userData.surname || '',
    p_role:             userData.role || 'employee',
    p_company:          userData.company || '',
    p_competency_level: userData.competencyLevel || 1,
    p_invited_by:       invitedByUserId || null,
  });

  if (!rpcError) {
    profile = rpcProfile;
  } else {
    // RPC not deployed yet — fall back to direct insert.
    // This will also fail if RLS is blocking it, surfacing a clear error.
    log.warn('admin_create_user_profile RPC not found — falling back to direct insert. Run sql/migrations/add_missing_user_columns.sql to fix.', rpcError.message);

    const fullInsert = {
      auth_id: authData.user?.id,
      email: userData.email.toLowerCase().trim(),
      first_name: userData.firstName || '',
      surname: userData.surname || '',
      role: userData.role || 'employee',
      company: userData.company || '',
      competency_level: userData.competencyLevel || 1,
      is_active: true,
      requires_password_change: true,
      invited_at: new Date().toISOString(),
      invited_by: invitedByUserId,
    };
    const coreInsert = {
      auth_id: authData.user?.id,
      email: userData.email.toLowerCase().trim(),
      first_name: userData.firstName || '',
      surname: userData.surname || '',
      role: userData.role || 'employee',
      is_active: true,
    };

    ({ data: profile, error: profileError } = await supabase
      .from('users')
      .insert(fullInsert)
      .select()
      .single());

    if (profileError) {
      const isMissingColumn = profileError.message?.includes('column') && profileError.message?.includes('schema cache');
      if (isMissingColumn) {
        ({ data: profile, error: profileError } = await supabase
          .from('users')
          .insert(coreInsert)
          .select()
          .single());
        if (!profileError && profile) {
          await supabase.from('users').update({ requires_password_change: true })
            .eq('id', profile.id).then(() => {}).catch(() => {});
        }
      }
    }
  }

  if (profileError) {
    log.error('Failed to create user profile:', profileError);
    if (profileError.code === '23505') {
      throw new Error('A user with this email already exists.');
    }
    throw profileError;
  }
  
  log.info('User created successfully:', userData.email);
  // No invite email sent — admin shares the temporary password directly with the user.
  // On first login the app will prompt them to change it (requires_password_change = true).
  
  return mapUserProfile(profile);
};

/**
 * Resend invite email to a pending user
 * Sends a password reset email so user can set their password
 * @param {string} email - User's email
 */
export const resendInvite = async (email) => {
  log.info('Resending invite to:', email);
  
  // Send password reset email
  const { error } = await supabase.auth.resetPasswordForEmail(
    email.toLowerCase().trim(),
    { redirectTo: `${window.location.origin}/reset-password` }
  );
  
  if (error) {
    log.error('Failed to resend invite:', error);
    if (error.message?.includes('only request this after') || error.message?.includes('rate limit') || error.message?.includes('too many requests')) {
      const secondsMatch = error.message.match(/after (\d+) seconds?/i);
      const wait = secondsMatch ? `Please wait ${secondsMatch[1]} seconds and try again.` : 'Please wait a minute and try again.';
      throw new Error(`Rate limit reached — invite email was just sent. ${wait}`);
    }
    throw error;
  }
  
  // Update invited_at
  await supabase
    .from('users')
    .update({ invited_at: new Date().toISOString() })
    .eq('email', email.toLowerCase().trim());
  
  log.info('Invite resent successfully');
};

/**
 * Request password reset
 * @param {string} email - User's email
 */
export const requestPasswordReset = async (email) => {
  log.info('Requesting password reset for:', email);
  
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  
  if (error) {
    log.error('Failed to request password reset:', error);
    throw error;
  }
  
  log.info('Password reset email sent');
};

/**
 * Admin: Send password reset email to a user
 * For helping users who can't sign in or forgot password
 * @param {string} email - User's email
 */
export const resetPasswordForUser = async (email) => {
  log.info('Admin resetting password for:', email);
  
  const { error } = await supabase.auth.resetPasswordForEmail(
    email.toLowerCase().trim(),
    { redirectTo: `${window.location.origin}/reset-password` }
  );
  
  if (error) {
    log.error('Failed to send password reset:', error);
    throw error;
  }
  
  log.info('Password reset email sent successfully');
};

/**
 * Update password (for logged-in user)
 * @param {string} newPassword - New password
 */
export const updatePassword = async (newPassword) => {
  log.info('Updating password');
  
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });
  
  if (error) {
    log.error('Failed to update password:', error);
    throw error;
  }
  
  // Clear the requires_password_change flag
  const { data: { user } } = await supabase.auth.getUser();
  if (user) {
    await supabase
      .from('users')
      .update({ requires_password_change: false })
      .eq('auth_id', user.id);
  }
  
  log.info('Password updated successfully');
};

/**
 * Update user profile (Admin/Team Leader)
 * @param {string} userId - users ID
 * @param {object} updates - Fields to update
 */
export const updateUserProfile = async (userId, updates) => {
  log.info('Updating user profile:', userId);
  
  // Compute display name from firstName/surname or legacy name field
  const displayName = [updates.firstName, updates.surname].filter(Boolean).join(' ') || updates.name || '';
  
  const fullUpdate = {
    first_name: updates.firstName,
    surname: updates.surname,
    role: updates.role,
    company: updates.company,
    is_active: updates.isActive,
    competency_level: updates.competencyLevel,
    updated_at: new Date().toISOString(),
    ...(updates.depotId !== undefined && { depot_id: updates.depotId || null }),
  };
  const coreUpdate = {
    first_name: updates.firstName,
    surname: updates.surname,
    role: updates.role,
    is_active: updates.isActive,
    updated_at: new Date().toISOString(),
  };

  let data, error;
  ({ data, error } = await supabase
    .from('users')
    .update(fullUpdate)
    .eq('id', userId)
    .select()
    .single());

  if (error) {
    const isMissingColumn = error.message?.includes('column') && error.message?.includes('schema cache');
    if (isMissingColumn) {
      log.warn('Extended user columns not yet in DB — retrying with core columns. Run sql/migrations/add_missing_user_columns.sql in Supabase to fix permanently.');
      ({ data, error } = await supabase
        .from('users')
        .update(coreUpdate)
        .eq('id', userId)
        .select()
        .single());
    }
  }

  if (error) {
    log.error('Failed to update user profile:', error);
    throw error;
  }
  
  log.info('User profile updated successfully');
  return mapUserProfile(data);
};

/**
 * Deactivate a user (Admin/Team Leader)
 * @param {string} userId - users ID
 */
export const deactivateUser = async (userId) => {
  log.info('Deactivating user:', userId);
  
  const { error } = await supabase
    .from('users')
    .update({ 
      is_active: false,
      updated_at: new Date().toISOString(),
    })
    .eq('id', userId);
  
  if (error) {
    log.error('Failed to deactivate user:', error);
    throw error;
  }
  
  log.info('User deactivated successfully');
};

/**
 * Reactivate a user (Admin/Team Leader)
 * @param {string} userId - users ID
 */
export const reactivateUser = async (userId) => {
  log.info('Reactivating user:', userId);
  
  const { error } = await supabase
    .from('users')
    .update({ 
      is_active: true,
      updated_at: new Date().toISOString(),
    })
    .eq('id', userId);
  
  if (error) {
    log.error('Failed to reactivate user:', error);
    throw error;
  }
  
  log.info('User reactivated successfully');
};

/**
 * Get all users (Admin/Team Leader)
 * @returns {Promise<object[]>}
 */
export const getAllUsers = async () => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .order('first_name', { ascending: true });
  
  if (error) {
    log.error('Failed to fetch users:', error);
    throw error;
  }
  
  return (data || []).map(mapUserProfile);
};

/**
 * Listen for auth state changes
 * @param {function} callback - Called with (event, session)
 * @returns {function} Unsubscribe function
 */
export const onAuthStateChange = (callback) => {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(callback);
  return () => subscription.unsubscribe();
};

// ============================================================
// Helper Functions
// ============================================================

/**
 * Generate a random temporary password
 * User will set their own password when they click the invite link
 */
function generateTempPassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
  let password = '';
  for (let i = 0; i < 16; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}

/**
 * Map database user record to application format
 */
function mapUserProfile(record) {
  if (!record) return null;
  
  // Determine status:
  // - 'inactive' if is_active is false (deactivated)
  // - 'pending' if invited but never logged in (has invited_at but no last_login)
  // - 'active' if has logged in at least once
  let status = 'active';
  if (record.is_active === false) {
    status = 'inactive';
  } else if (record.invited_at && !record.last_login) {
    // User was invited but hasn't logged in yet
    status = 'pending';
  }
  
  // Compute display name from first_name + surname, falling back to name, then username
  const firstName = record.first_name || '';
  const surname = record.surname || '';
  const fullName = [firstName, surname].filter(Boolean).join(' ');
  const displayName = fullName || record.email?.split('@')[0] || 'Unknown';
  
  return {
    id: record.id,
    dbId: record.id, // For compatibility with existing code
    authId: record.auth_id,
    username: record.email?.split('@')[0] || '',
    email: record.email,
    firstName: firstName,
    surname: surname,
    name: displayName, // Combined display name
    role: record.role,
    company: record.company,
    // Note: Company logo is managed via SystemSettings → localStorage, not per-user DB columns
    isActive: record.is_active !== false, // Treat null as active
    isPlatformOwner: record.is_platform_owner === true, // Hidden owner account
    requiresPasswordChange: record.requires_password_change,
    lastLogin: record.last_login ? new Date(record.last_login) : null,
    invitedAt: record.invited_at ? new Date(record.invited_at) : null,
    invitedBy: record.invited_by,
    createdAt: record.created_at ? new Date(record.created_at) : null,
    competencyLevel: record.competency_level || 1, // Default to Trainee (1)
    status,
    // Compliance settings (organization-level)
    complianceMode: record.compliance_mode ?? 'flexible',
    customGracePeriods: record.custom_grace_periods ?? null,
    // Missed tests visibility: 'full', 'reports-only', 'off'
    missedTestsVisibility: record.missed_tests_visibility ?? 'full',
  };
}
